// AutomationConfigForm.js
// The configuration half of an Automation — WHEN it runs and the PARAMETERS it
// runs with — as one form over a plain configuration object, shared by the
// Automations tab's editor (which wraps it in a lease-guarded draft) and the
// View's "Keep current" dialog (one form per card). Every surface here derives
// from the PUBLISHED version's data-source blocks the caller passes in, never
// from a draft.
//
// The configuration object is `{ parameters, on_schedule_config,
// on_new_data_config }` in the shapes the trigger document stores;
// `onChange` receives a patch of whichever of those fields changed and the
// caller merges it into its own state.
//
// Controls start per source. Unify combines only filters whose definitions,
// current values and IS/NOT operators agree; other controls stay per source.

import React, { useMemo, useRef, useState } from 'react';
import {
  Box,
  Chip,
  Divider,
  FormControl,
  FormControlLabel,
  Switch,
  MenuItem,
  Select,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { useAttributeOptions } from '../../contexts/AttributesContext';
import TriggerScheduleConfig from '../functions/TriggerScheduleConfig';
import ParameterField from '../common/ParameterField';
import AppDatePicker from '../common/AppDatePicker';
import DateDraftScope from '../common/DateDraftScope';
import { formatText } from '../common/TextFormatterProvider';
import {
  cleanRangeValue,
  formatParameterInputValue,
  isDateRangeParam,
  isStartDateParam,
  isEndDateParam,
  parameterLabel,
  isRangeParamValue,
  resolveParameterType,
  filterSignature,
  DYNAMIC_DATE_TOKENS,
  prepareRunParameters,
} from '../../utils/functionParameterUtils';
import { isExternalTable } from '../../utils/tableTypes';

export const isDynamicValue = (value) => typeof value === 'string' && value.startsWith('dynamic:');

// The two bounds of a range on an ordinary Date column, each of which can
// follow the calendar on its own (M152).
const RANGE_BOUNDS = [
  { key: 'gte', label: 'From', defaultToken: 'dynamic:start_of_month' },
  { key: 'lte', label: 'To', defaultToken: 'dynamic:today' },
];

// Where a column's value lives in a block's saved parameters: under its own
// name when the filter includes matching rows, under `exclude_filters` when it
// excludes them.
export const isExcludedIn = (blockParams, column) => (
  blockParams?.exclude_filters !== null
  && typeof blockParams?.exclude_filters === 'object'
  && blockParams.exclude_filters[column] !== undefined
);

export const schemasById = (tables) => {
  const schemas = {};
  (tables || []).forEach((table) => {
    if (table?.id) schemas[table.id] = table;
  });
  return schemas;
};

// The only tables an "on new data" automation may watch: the ones this version
// actually reads, minus external tables, which Decode reads through rather
// than stores and which therefore never receive an upload.
export const watchableTablesFor = (dataSourceBlocks, tables) => {
  const readIds = new Set(
    Object.values(dataSourceBlocks || {}).map((block) => block.table_id).filter(Boolean),
  );
  return (tables || []).filter((table) => readIds.has(table.id) && !isExternalTable(table));
};

export const buildVariableParams = (dataSourceBlocks, tableSchemas, { unify = false, parameters = {} } = {}) => {
  const paramsMap = {};
  Object.entries(dataSourceBlocks || {}).forEach(([blockId, block]) => {
    const tableId = block.table_id;
    (block.filters || []).forEach((filter) => {
      if (!filter?.variable || !filter.column) return;
      if (!paramsMap[filter.column]) {
        paramsMap[filter.column] = {
          key: filter.column,
          name: filter.column,
          label: parameterLabel(filter.column),
          blockIds: [],
          tableIds: [],
          type: resolveParameterType(filter.column, filter.type),
          isAttribute: false,
          isDateRange: isDateRangeParam(filter.column),
          isRange: false,
          signatures: new Set(),
          perBlock: [],
        };
      }
      const param = paramsMap[filter.column];
      param.blockIds.push(blockId);
      if (filter.range) param.isRange = true;
      if (tableId && !param.tableIds.includes(tableId)) param.tableIds.push(tableId);
      const schema = tableSchemas?.[tableId];
      const metadataColumns = [
        ...(schema?.attributeColumns || []),
        ...(schema?.tableAttributeColumns || []),
      ];
      const isAttribute = metadataColumns.some((column) => column?.name === filter.column);
      if (isAttribute) param.isAttribute = true;
      param.signatures.add(filterSignature(filter));
      param.perBlock.push({
        key: `${blockId}::${filter.column}`,
        name: filter.column,
        label: `${parameterLabel(filter.column)} (${block.name || blockId})`,
        blockIds: [blockId],
        tableIds: tableId ? [tableId] : [],
        type: resolveParameterType(filter.column, filter.type),
        isAttribute,
        isDateRange: isDateRangeParam(filter.column),
        isRange: Boolean(filter.range),
      });
    });
  });
  const params = Object.values(paramsMap).flatMap((param) => {
    const { signatures, perBlock, ...merged } = param;
    const current = param.blockIds.map((blockId) => {
      const values = parameters[blockId] || {};
      const excluded = isExcludedIn(values, param.name);
      const fallback = param.isRange ? {} : (param.isDateRange ? '' : []);
      const value = param.isRange
        ? values.range_filters?.[param.name]
        : (excluded ? values.exclude_filters[param.name] : values[param.name]);
      return JSON.stringify([excluded, value ?? fallback]);
    });
    const agrees = current.every((value) => value === current[0]);
    return unify && signatures.size === 1 && agrees ? [merged] : perBlock;
  });
  return params.sort((a, b) => {
    const weight = (param) => {
      if (isStartDateParam(param.name)) return 0;
      if (isEndDateParam(param.name)) return 1;
      return 2;
    };
    return weight(a) - weight(b) || a.name.localeCompare(b.name) || a.label.localeCompare(b.label);
  });
};

function AutomationConfigForm({
  // 'on_schedule' | 'on_new_data'
  triggerType,
  // { parameters, on_schedule_config, on_new_data_config }
  config,
  // Receives a patch of the field(s) that changed.
  onChange,
  dataSourceBlocks,
  tables = [],
  publishedVersion,
  editable,
  // A DateDraftScope ref: the caller's Save refuses while any date picker in
  // this form is half-typed, and reveals them.
  dateScopeRef,
  // A View's Keep-current dialog owns WHEN its automations run and shows only
  // the parameters.
  showWhen = true,
}) {
  // The calendar rule a single-date parameter followed before it was switched
  // to a fixed date, restored if it is switched back.
  const rememberedDynamicRef = useRef({});
  const localDateScopeRef = useRef(null);
  const [unifyParameters, setUnifyParameters] = useState(false);
  const parameters = config?.parameters || {};
  const prepared = useMemo(() => prepareRunParameters(parameters, dataSourceBlocks, {
    allowDynamicDates: true,
  }), [parameters, dataSourceBlocks]);

  const tableSchemas = useMemo(() => schemasById(tables), [tables]);
  const watchableTables = useMemo(
    () => watchableTablesFor(dataSourceBlocks, tables),
    [dataSourceBlocks, tables],
  );
  const variableParams = useMemo(
    () => buildVariableParams(dataSourceBlocks, tableSchemas, { unify: unifyParameters, parameters }),
    [dataSourceBlocks, tableSchemas, unifyParameters, parameters],
  );
  const attributeColumnNames = useMemo(
    () => variableParams.filter((param) => param.isAttribute).map((param) => param.name),
    [variableParams],
  );
  // Their value lists, live from the AttributesProvider.
  const attributeValues = useAttributeOptions(attributeColumnNames);

  const updateParameters = (mutate) => {
    const next = { ...parameters };
    mutate(next);
    onChange({ parameters: next });
  };

  const handleParameterChange = (param, value) => {
    updateParameters((next) => {
      param.blockIds.forEach((blockId) => {
        const blockParams = { ...(next[blockId] || {}) };
        if (param.isRange) {
          blockParams.range_filters = {
            ...(blockParams.range_filters || {}),
            [param.name]: cleanRangeValue(value),
          };
          delete blockParams[param.name];
        } else if (isExcludedIn(blockParams, param.name)) {
          blockParams.exclude_filters = { ...blockParams.exclude_filters, [param.name]: value };
        } else {
          blockParams[param.name] = value;
        }
        next[blockId] = blockParams;
      });
    });
  };

  // IS ↔ NOT: the column's values move between the block's own map and its
  // `exclude_filters`, which is how the runner reads an exclusion.
  const handleToggleExclude = (param, currentlyExcluded) => {
    updateParameters((next) => {
      param.blockIds.forEach((blockId) => {
        const blockParams = { ...(next[blockId] || {}) };
        const excludes = { ...(blockParams.exclude_filters || {}) };
        if (currentlyExcluded) {
          blockParams[param.name] = excludes[param.name] ?? [];
          delete excludes[param.name];
        } else {
          excludes[param.name] = blockParams[param.name] ?? [];
          delete blockParams[param.name];
        }
        if (Object.keys(excludes).length > 0) blockParams.exclude_filters = excludes;
        else delete blockParams.exclude_filters;
        next[blockId] = blockParams;
      });
    });
  };

  // Dynamic → Fixed starts with no date bound; Fixed → Dynamic restores the
  // calendar rule this control last used.
  const handleDateModeToggle = (param, value) => {
    if (isDynamicValue(value)) {
      rememberedDynamicRef.current[param.key] = value;
      handleParameterChange(param, '');
    } else {
      handleParameterChange(param, rememberedDynamicRef.current[param.key] || 'dynamic:today');
    }
  };

  // The same switch for one bound of a Date range. A bound switched to Fixed
  // and left blank leaves that side of the range open.
  const handleRangeBoundModeToggle = (param, range, bound) => {
    const key = `${param.key}::${bound.key}`;
    const current = range[bound.key] ?? '';
    if (isDynamicValue(current)) {
      rememberedDynamicRef.current[key] = current;
      handleParameterChange(param, { ...range, [bound.key]: '' });
    } else {
      handleParameterChange(param, { ...range, [bound.key]: rememberedDynamicRef.current[key] || bound.defaultToken });
    }
  };

  // A unified control contains only blocks that agree (buildVariableParams).
  // Missing values are empty constraints, including on a saved automation.
  const parameterValue = (param) => {
    const fallback = param.isRange ? { gte: '', lte: '' } : (param.isDateRange ? '' : []);
    const values = param.blockIds.map((blockId) => {
      const blockParams = parameters[blockId];
      let value;
      if (param.isRange) value = blockParams?.range_filters?.[param.name];
      else if (isExcludedIn(blockParams, param.name)) value = blockParams.exclude_filters[param.name];
      else value = blockParams?.[param.name];
      return value === undefined ? fallback : value;
    });
    return values[0] ?? fallback;
  };

  const isExcluded = (param) => param.blockIds.every((blockId) => (
    isExcludedIn(parameters[blockId], param.name)
  ));

  // A text column is searched on the server only when its table indexes it, and
  // never for an external table, which cannot be indexed.
  const indexedTableIdsFor = (param) => (param.tableIds || []).filter((tableId) => (
    param.type === 'Text'
    && !isExternalTable(tableSchemas[tableId])
    && tableSchemas[tableId]?.detailColumns?.some((column) => column?.name === param.name && column?.index)
  ));

  return (
    <>
      {showWhen && <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>When</Typography>}
      {!showWhen ? null : triggerType === 'on_new_data' ? (
        <Box sx={{ opacity: editable ? 1 : 0.6 }}>
          <Typography variant="caption" color="text.secondary">
            Runs when new data is uploaded to
          </Typography>
          <FormControl fullWidth size="small" sx={{ mt: 0.5 }}>
            <Select
              value={watchableTables.some((table) => table.id === config?.on_new_data_config?.table_id)
                ? config.on_new_data_config.table_id
                : ''}
              disabled={!editable}
              displayEmpty
              onChange={(e) => onChange({
                on_new_data_config: { ...(config?.on_new_data_config || {}), table_id: e.target.value },
              })}
            >
              <MenuItem value="" disabled>Choose a table</MenuItem>
              {watchableTables.map((table) => (
                <MenuItem key={table.id} value={table.id}>{formatText(table.name || table.id)}</MenuItem>
              ))}
            </Select>
          </FormControl>
          {watchableTables.length === 0 && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              This function reads no table that receives uploads, so it can only run on a schedule.
            </Typography>
          )}
        </Box>
      ) : (
        <Box sx={{ opacity: editable ? 1 : 0.6 }}>
          <TriggerScheduleConfig
            value={config?.on_schedule_config}
            disabled={!editable}
            onChange={(nextConfig) => onChange({ on_schedule_config: nextConfig })}
          />
        </Box>
      )}

      {variableParams.length > 0 && (
        <>
          {showWhen && <Divider sx={{ my: 2 }} />}
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography variant="subtitle2" fontWeight={600}>Parameters</Typography>
            <FormControlLabel
              label="Unify"
              control={<Switch size="small" checked={unifyParameters} onChange={(event) => {
                if (!localDateScopeRef.current?.reveal()) setUnifyParameters(event.target.checked);
              }} />}
            />
          </Stack>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 1.5, display: 'block' }}>
            {`The values this automation runs with, from published version ${publishedVersion}. Date fields can follow the calendar instead of a fixed day.`}
          </Typography>
          <DateDraftScope ref={(scope) => {
            localDateScopeRef.current = scope;
            if (typeof dateScopeRef === 'function') dateScopeRef(scope);
            else if (dateScopeRef) dateScopeRef.current = scope;
          }}>
          <Box sx={{ opacity: editable ? 1 : 0.6 }}>
            {variableParams.map((param) => {
              const value = parameterValue(param);
              const dynamic = isDynamicValue(value);
              const error = prepared.errors.find((issue) => (
                issue.column === param.name && param.blockIds.includes(issue.blockId)
              ))?.message || '';
              // A single-date parameter is the one control an automation needs
              // that a run does not: a date it re-resolves at every firing.
              if (param.isDateRange && !param.isRange) {
                return (
                  <Box key={param.key} sx={{ mb: 2 }}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.75 }}>
                      <Typography variant="body2" noWrap sx={{ minWidth: 0 }}>
                        {param.label.replace(/_/g, ' ')}
                      </Typography>
                      <Tooltip title={dynamic
                        ? 'Re-resolved every time this automation runs — click to pin one date'
                        : 'A fixed date — click to follow the calendar instead'}>
                        <Chip
                          size="small"
                          label={dynamic ? 'Dynamic' : 'Fixed'}
                          color={dynamic ? 'primary' : 'default'}
                          variant={dynamic ? 'filled' : 'outlined'}
                          disabled={!editable}
                          onClick={editable ? () => handleDateModeToggle(param, value) : undefined}
                          sx={{ cursor: editable ? 'pointer' : 'default' }}
                        />
                      </Tooltip>
                    </Stack>
                    {dynamic ? (
                      <FormControl fullWidth size="small">
                        <Select
                          value={value}
                          disabled={!editable}
                          onChange={(e) => handleParameterChange(param, e.target.value)}
                        >
                          {DYNAMIC_DATE_TOKENS.map((token) => (
                            <MenuItem key={token.value} value={token.value}>{token.label}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    ) : (
                      <AppDatePicker
                        fullWidth
                        disabled={!editable}
                        value={formatParameterInputValue(value, param.type, { multi: false, dateRange: true })}
                        onChange={(nextDate) => handleParameterChange(param, nextDate)}
                      />
                    )}
                    {error && <Typography role="alert" variant="caption" color="error">{error}</Typography>}
                  </Box>
                );
              }
              // A range on an ordinary Date column: either bound can follow
              // the calendar, resolved at every firing exactly as a single
              // date is (M152).
              if (param.isRange) {
                const range = isRangeParamValue(value) ? value : { gte: '', lte: '' };
                return (
                  <Box key={param.key} sx={{ mb: 2 }}>
                    <Typography variant="body2" noWrap sx={{ mb: 0.75, minWidth: 0 }}>
                      {param.label.replace(/_/g, ' ')}
                    </Typography>
                    <Stack spacing={1}>
                      {RANGE_BOUNDS.map((bound) => {
                        const boundValue = range[bound.key] ?? '';
                        const boundDynamic = isDynamicValue(boundValue);
                        return (
                          <Box key={bound.key}>
                            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
                              <Typography variant="caption" color="text.secondary">{bound.label}</Typography>
                              <Tooltip title={boundDynamic
                                ? 'Re-resolved every time this automation runs — click to pin one date'
                                : 'A fixed date — click to follow the calendar instead'}>
                                <Chip
                                  size="small"
                                  label={boundDynamic ? 'Dynamic' : 'Fixed'}
                                  color={boundDynamic ? 'primary' : 'default'}
                                  variant={boundDynamic ? 'filled' : 'outlined'}
                                  disabled={!editable}
                                  onClick={editable ? () => handleRangeBoundModeToggle(param, range, bound) : undefined}
                                  sx={{ cursor: editable ? 'pointer' : 'default' }}
                                />
                              </Tooltip>
                            </Stack>
                            {boundDynamic ? (
                              <FormControl fullWidth size="small">
                                <Select
                                  value={boundValue}
                                  disabled={!editable}
                                  onChange={(e) => handleParameterChange(param, { ...range, [bound.key]: e.target.value })}
                                >
                                  {DYNAMIC_DATE_TOKENS.map((token) => (
                                    <MenuItem key={token.value} value={token.value}>{token.label}</MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            ) : (
                              <AppDatePicker
                                fullWidth
                                disabled={!editable}
                                value={boundValue}
                                onChange={(nextDate) => handleParameterChange(param, { ...range, [bound.key]: nextDate })}
                              />
                            )}
                          </Box>
                        );
                      })}
                    </Stack>
                    {error && <Typography role="alert" variant="caption" color="error">{error}</Typography>}
                  </Box>
                );
              }
              const excluded = isExcluded(param);
              return (
                <ParameterField
                  key={param.key}
                  name={param.name}
                  label={param.label}
                  error={error}
                  value={value}
                  onChange={(next) => handleParameterChange(param, next)}
                  disabled={!editable}
                  type={param.type}
                  isRange={param.isRange}
                  isAttribute={param.isAttribute}
                  availableValues={attributeValues[param.name]}
                  indexedTableIds={indexedTableIdsFor(param)}
                  excluded={excluded}
                  onToggleExclude={() => handleToggleExclude(param, excluded)}
                />
              );
            })}
          </Box>
          </DateDraftScope>
        </>
      )}
    </>
  );
}

export default AutomationConfigForm;
